/**
 * 品目カード（SPEC §7.1）。
 * [購入] [開封] [使い切った] の 3 ボタン。開封と使い切りは確認なしの即実行で、
 * 取り消しは Undo スナックバーで受ける（SPEC §7.7）。
 * 名前の行をタップすると品目詳細へ。
 */
import type { ReactNode } from 'react';
import type { Forecast } from '../../shared/calc';
import { daysInclusive, formatMd, today } from '../lib/date';
import type { ItemView } from '../hooks/useAppData';

export interface ItemCardProps {
  view: ItemView;
  forecast?: Forecast;
  onOpenDetail: () => void;
  onPurchase: () => void;
  onOpen: () => void;
  onFinish: () => void;
}

export function ItemCard({
  view,
  forecast,
  onOpenDetail,
  onPurchase,
  onOpen,
  onFinish,
}: ItemCardProps): ReactNode {
  const { item, stock, openLots, hasUnlinkedLot } = view;
  const t = today();
  const inUse = openLots.length > 0;
  const displayStock = Math.max(0, stock);

  return (
    <article className="rounded-2xl border border-ink-700 bg-ink-850 p-4">
      <button type="button" onClick={onOpenDetail} className="flex w-full items-start gap-3 text-left">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-bold">{item.name}</h2>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-zinc-500">
            {item.category && <span>{item.category}</span>}
            {forecast && <ForecastLabel forecast={forecast} />}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[11px] leading-none text-zinc-500">未開封</div>
          <div
            className={`text-2xl font-bold leading-tight tabular-nums ${
              displayStock === 0 ? 'text-red-400' : 'text-zinc-100'
            }`}
          >
            {displayStock}
          </div>
        </div>
      </button>

      {(inUse || hasUnlinkedLot) && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {inUse && (
            <span className="chip bg-orange-500/15 text-orange-300">
              使用中 {openLots.length > 1 ? `${openLots.length}本` : ''}
              <span className="text-orange-400/70">
                {formatMd(openLots[0].opened_at)}〜{daysInclusive(openLots[0].opened_at, t)}日目
              </span>
            </span>
          )}
          {hasUnlinkedLot && (
            <span className="chip bg-amber-500/10 text-amber-300/90" title="在庫0のまま開封された記録があります">
              購入記録なし
            </span>
          )}
        </div>
      )}

      <div className={`mt-3.5 grid gap-2 ${inUse ? 'grid-cols-3' : 'grid-cols-2'}`}>
        <button type="button" className="btn-ghost" onClick={onPurchase}>
          購入
        </button>
        <button type="button" className="btn-ghost" onClick={onOpen}>
          開封
        </button>
        {inUse && (
          <button
            type="button"
            className="btn bg-orange-500/90 text-ink-950 hover:bg-orange-400"
            onClick={onFinish}
          >
            使い切った
          </button>
        )}
      </div>
    </article>
  );
}

/** 「あと◯日」（SPEC §6.3 / §7.1）。実績が無い間は推定であることを示す。 */
function ForecastLabel({ forecast }: { forecast: Forecast }): ReactNode {
  const urgent = forecast.daysLeft <= 3;
  const soon = forecast.daysLeft <= 7;
  return (
    <span
      className={`font-semibold ${urgent ? 'text-red-400' : soon ? 'text-amber-300' : 'text-zinc-400'}`}
      title={
        forecast.basis === 'history'
          ? `確定ロット ${forecast.sample} 件の平均 ${forecast.averageDurationDays.toFixed(1)} 日から算出`
          : '使い切り履歴がまだ無いので想定使用日数から算出'
      }
    >
      {forecast.daysLeft === 0 ? '在庫切れ' : `あと${forecast.daysLeft}日`}
      {forecast.basis === 'default' && <span className="ml-0.5 font-normal text-zinc-600">(想定)</span>}
    </span>
  );
}
